import { SmartCheckbox } from 'app/components/shared/FormComponents/SmartCheckbox'
import { SmartCurrencyInput } from 'app/components/shared/FormComponents/SmartCurrencyInput'
import { SmartInput } from 'app/components/shared/FormComponents/SmartInput'
import { FormGroup } from 'app/components/shared/FormGroup'
import { FormSection } from 'app/components/shared/FormSection'
import { FormTooltip } from 'app/components/shared/FormTooltip'
import { Container } from 'app/components/ui/Container'
import { Grid } from 'app/components/ui/Grid'
import { Text } from 'app/components/ui/Text'
import { useLocationFields } from 'app/hooks/useLocationFields'
import {
  SponsorRequestScope,
  SponsorRequestTimeline,
  Location,
} from 'EstraSponsor'
import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import styled, { useTheme } from 'styled-components'

import { SponsorRequestDataInnerFormEvents } from './SponsorRequestDataFormInnerEvents'

const sponsorRequestScopes: {
  [key in SponsorRequestScope]: string
} = {
  sport: 'Sport',
  culture: 'Cultura',
  event: 'Evento',
  'e-game': 'E-games',
}

const sponsorRequestTimeline: {
  [key in SponsorRequestTimeline]: string
} = {
  single: 'Singola',
  multiple: 'Multipla',
  season: 'Stagione',
}

type Props = {
  locations: Location[]
  isMultiRegionaleOn: boolean
}

export const SponsorRequestDataInnerForm = ({
  locations,
  isMultiRegionaleOn,
}: Props): JSX.Element => {
  const theme = useTheme()

  const { watch, trigger } = useFormContext()
  const timelineWatcher = watch('timeline') as SponsorRequestTimeline | null
  const regionIdWatcher = watch('region_id')
  const provinceIdWatcher = watch('province_id')

  const [isMultiRegionaleSelected, setIsMultiRegionaleSelected] =
    useState(false)

  useEffect(() => {
    isMultiRegionaleOn && setIsMultiRegionaleSelected(true)
  }, [isMultiRegionaleOn])

  const { regionOptions, provinceOptions, cityOptions } = useLocationFields({
    locations,
    selectedRegionId: regionIdWatcher,
    selectedProvinceId: provinceIdWatcher,
    provincesOnly: false,
  })

  const handleChangeSelectRegion = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value
    value === '21'
      ? setIsMultiRegionaleSelected(true)
      : setIsMultiRegionaleSelected(false)
  }

  return (
    <Container>
      <Text variant='s12' mb={2}>
        (<StyledSpan>*</StyledSpan>) I campi contrassegnati con l&apos;asterisco
        sono obbligatori.
      </Text>
      {/* Descrizione Iniziativa */}
      <FormSection title='Descrizione Iniziativa'>
        <Grid cols={[1, 1]}>
          <FormGroup>
            <SmartInput label='Nome' type='text' name='name' labelRequired />
          </FormGroup>
          <FormGroup>
            <SmartInput
              label='Ambito'
              type='select'
              name='scope'
              labelRequired
              selectCaretSize='small'
            >
              <option value={''}>Seleziona</option>
              {Object.keys(sponsorRequestScopes).map((optValue) => (
                <option key={optValue} value={optValue}>
                  {sponsorRequestScopes[optValue as SponsorRequestScope]}
                </option>
              ))}
            </SmartInput>
          </FormGroup>
        </Grid>
        <Grid cols={[1, 1]}>
          <FormGroup>
            <SmartInput
              label='Descrizione'
              type='textarea'
              name='purpose'
              labelRequired
            />
          </FormGroup>
          <FormTooltip tooltipText={`Es. aziende, famiglie, bambini, soci...`}>
            <FormGroup>
              <SmartInput
                label='Pubblico di riferimento'
                type='text'
                name='target_audience'
                placeholder='Es. aziende, famiglie, bambini, soci...'
                labelRequired
              />
            </FormGroup>
          </FormTooltip>
        </Grid>
        <Grid cols={[1, 1]}>
          <FormTooltip tooltipText={`Deve cominciare con http o https`}>
            <FormGroup>
              <SmartInput
                label='Sito web'
                type='text'
                name='website_link'
                placeholder='https://www.estra.it'
              />
            </FormGroup>
          </FormTooltip>
          <FormGroup>
            <SmartInput
              label='Pagina facebook'
              type='text'
              name='facebook_link'
              placeholder='https://www.facebook.com/EstraSpa'
            />
          </FormGroup>
          <FormGroup>
            <SmartInput
              label='Pagina instagram'
              type='text'
              name='instagram_link'
              placeholder='https://www.instagram.com/estra_spa'
            />
          </FormGroup>
          <FormGroup>
            <SmartInput
              label='Profilo twitter'
              type='text'
              name='twitter_link'
              placeholder='https://twitter.com/estraspa'
            />
          </FormGroup>
          <FormGroup>
            <SmartInput
              label='Youtube'
              type='text'
              name='youtube_link'
              placeholder='https://www.youtube.com/user/EstraSpA'
            />
          </FormGroup>
          <FormGroup>
            <SmartInput
              label='Profilo TikTok'
              type='text'
              name='tiktok_link'
              placeholder='https://www.tiktok.com/@username'
            />
          </FormGroup>
        </Grid>
      </FormSection>

      {/* Date evento */}
      <FormSection title='Date evento'>
        <Grid cols={[1, 1]}>
          <FormGroup>
            <SmartInput
              label='Tipo evento'
              type='select'
              name='timeline'
              labelRequired
              selectCaretSize='small'
            >
              <option value={''}>Seleziona</option>
              {Object.keys(sponsorRequestTimeline).map((optValue) => (
                <option key={optValue} value={optValue}>
                  {sponsorRequestTimeline[optValue as SponsorRequestTimeline]}
                </option>
              ))}
            </SmartInput>
          </FormGroup>
          <FormGroup>
            <SmartInput
              label='Numero spettatori precedenti edizioni'
              type='text'
              name='previous_edition_attendees'
              maxLength={20}
            />
          </FormGroup>
        </Grid>
        <Grid cols={[1, 1]}>
          {timelineWatcher === 'season' && (
            <>
              <FormGroup>
                <SmartInput
                  label='Numero di eventi per stagione'
                  type='text'
                  name='nr_events_for_season'
                />
              </FormGroup>
              <FormGroup>
                <SmartInput
                  label='Numero spettatori medio'
                  type='text'
                  name='attendees_average_for_event'
                />
              </FormGroup>
            </>
          )}
        </Grid>
        <SponsorRequestDataInnerFormEvents></SponsorRequestDataInnerFormEvents>
      </FormSection>

      {/* Richiesta */}
      <FormSection title='Richiesta'>
        <Grid cols={[1, 1]}>
          <FormGroup>
            <SmartInput
              type='select'
              label='Regione'
              name='region_id'
              labelRequired
              selectCaretSize='small'
              onChange={handleChangeSelectRegion}
            >
              <option value={''}>Seleziona</option>

              {regionOptions?.map((region) => {
                if (region.value === '21') {
                  return (
                    <>
                      <option key={region.value} value={region.value}>
                        {region.label}
                      </option>
                      <option value={''} disabled>
                        -----------------------------
                      </option>
                    </>
                  )
                } else {
                  return (
                    <option key={region.value} value={region.value}>
                      {region.label}
                    </option>
                  )
                }
              })}
            </SmartInput>
          </FormGroup>
          <FormGroup>
            <SmartInput
              type='select'
              label='Provincia'
              name='province_id'
              labelRequired
              selectCaretSize='small'
              disabled={isMultiRegionaleSelected}
            >
              <option value={''}>Seleziona</option>
              {provinceOptions.map((province) => (
                <option key={province.value} value={province.value}>
                  {province.label}
                </option>
              ))}
            </SmartInput>
          </FormGroup>

          <FormGroup>
            <SmartInput
              type='select'
              label='Comune'
              name='city_id'
              selectCaretSize='small'
              disabled={isMultiRegionaleSelected || !cityOptions.length}
            >
              <option value={''}>Seleziona</option>
              {cityOptions.map((city) => (
                <option key={city.value} value={city.value}>
                  {city.label}
                </option>
              ))}
            </SmartInput>
          </FormGroup>

          <FormTooltip
            tooltipText={`Nome e indirizzo del luogo in cui sarà effettuato l'evento/attività. Es Palazzetto Rossi Via Roma 5 Milano, oppure, Piazza Garibaldi Roma...`}
          >
            <FormGroup>
              <SmartInput
                label='Luogo svolgimento'
                type='text'
                name='place'
                labelRequired
              />
            </FormGroup>
          </FormTooltip>
        </Grid>
        <FormGroup>
          <Text variant='s14' color={theme.color.primari.grigioEstra}>
            N.B: Inserire solo un tipo di contributo, economico o tecnico.
          </Text>
        </FormGroup>
        <Grid cols={[1, 1]}>
          <FormGroup>
            <SmartCurrencyInput
              label='Contributo Economico'
              name='financial_contribution'
              // Revalidate on change
              onChange={() => {
                trigger('technical_contribution')
                trigger('financial_contribution')
              }}
            />
          </FormGroup>
          <FormTooltip
            tooltipText={`Materiale brandizzato o altre forniture tecniche.`}
          >
            <FormGroup>
              <SmartInput
                label='Contributo Tecnico'
                type='textarea'
                name='technical_contribution'
                onChange={() => {
                  trigger('technical_contribution')
                  trigger('financial_contribution')
                }}
              />
            </FormGroup>
          </FormTooltip>
        </Grid>
        <Grid cols={[1, 1]}>
          <FormTooltip
            tooltipText={`Flyer/brochure/programma, Biglietti/abbonamenti, Affissioni,Sito web, Abbigliamento, Backdrop interviste, Gonfiabile, Gadget, TNT, Vele/bandiere, LED. Newsletter, SMS/Snapchat/Whatsapp,Social media (Facebook/Twitter/Instagram/etc), Diffusione spot video, Diffusione spot audio, Comunicato stampa,Pagina promozionale dedicata su brochure/giornale, Intervista,Stand commerciale, Brandizzazione stadio/palazzetto.`}
          >
            <FormGroup>
              <SmartInput
                label='Visibilità e comunicazione del brand'
                type='textarea'
                name='logo_visibility'
                labelRequired
              />
            </FormGroup>
          </FormTooltip>
          <FormTooltip
            tooltipText={`Biglietti/abbonamenti, premio Estra, hospitality, altro.`}
          >
            <FormGroup>
              <SmartInput
                label='Benefit per lo sponsor'
                type='textarea'
                name='benefits'
              />
            </FormGroup>
          </FormTooltip>
        </Grid>
      </FormSection>

      {/* Ufficio Stampa */}
      <FormSection title='Ufficio Stampa'>
        <Grid cols={[1, 1, 1]}>
          <FormGroup>
            <SmartInput
              label='Nome ufficio stampa'
              type='text'
              name='press_office_name'
            />
          </FormGroup>
          <FormGroup>
            <SmartInput
              label='Email ufficio stampa'
              type='text'
              name='press_office_email'
            />
          </FormGroup>
          <FormGroup>
            <SmartInput
              label='Telefono ufficio stampa'
              type='text'
              name='press_office_phone_number'
            />
          </FormGroup>
        </Grid>
      </FormSection>

      {/* Contatto commerciale */}
      <FormSection title='Contatto commerciale'>
        <Grid cols={[1, 1, 1]}>
          <FormGroup>
            <SmartInput
              label='Nome Referente'
              type='text'
              name='commercial_referent_name'
              labelRequired
            />
          </FormGroup>
          <FormGroup>
            <SmartInput
              label='Email Referente'
              type='text'
              name='commercial_referent_email'
              labelRequired
            />
          </FormGroup>
          <FormGroup>
            <SmartInput
              label='Telefono Referente'
              type='text'
              name='commercial_referent_phone_number'
              labelRequired
            />
          </FormGroup>
        </Grid>
        <Text variant='s14' color={theme.color.primari.grigioEstra}>
          Si chiede di inserire un nominativo che potrà essere ricontattato da
          Estra o società del gruppo Estra S.p.A. per la promozione di
          eventuali opportunità commerciali.
        </Text>
      </FormSection>
      <FormSection title='Accettazioni'>
        <Text variant='s14' color={theme.color.primari.grigioEstra}>
          {
            "Lo Sponsee dichiara e dà atto di conoscere il il Codice Etico e di Condotta, Politica Diritti Umani e la Politica anticorruzione del Gruppo Estra S.p.A. e si impegna, per quanto di propria competenza e a pena di risoluzione della Sponsorizzazione che potrà essere eventualmente concessa, ai sensi e per gli effetti dell'art. 1456 c.c. a non porre in essere atti o comportamenti tali da determinare una violazione degli stessi. Tale impegno viene espressamente assunto anche ai sensi e per gli effetti dell'art. 1381 c.c. con riferimento alle attività svolte dal proprio personale o dai propri collaboratori a qualsiasi titolo. Le premesse e gli atti richiamati formano parte integrante e sostanziale del presente contratto."
          }
        </Text>
        <Spacer />
        <FormGroup>
          <SmartCheckbox name='ethic_code_accepted'>
            Ho letto ed accetto quanto stabilito dal
            <a
              rel='noreferrer'
              target='_blank'
              href='https://res.cloudinary.com/estra/image/upload/v1692950432/documenti/codice-etico/Codice_Etico_2023.pdf'
            >
              {' '}
              Codice Etico e di Condotta,{' '}
            </a>
            Politica Diritti Umani
          </SmartCheckbox>
          <Spacer />
          <SmartCheckbox name='anti_corruption_policy_accepted'>
            Ho letto ed accetto quanto stabilito dalla{' '}
            <a
              rel='noreferrer'
              target='_blank'
              href='https://res.cloudinary.com/estra/image/upload/v1696240609/documenti/codice-etico/Politica_Anticorruzione.pdf'
            >
              Politica Anticorruzione
            </a>
          </SmartCheckbox>
        </FormGroup>
      </FormSection>
    </Container>
  )
}

const StyledSpan = styled.span`
  color: ${({ theme }) => theme.color.secondari.arancioneScuro};
`

const Spacer = styled.div`
  margin-top: 1rem;
`
